import { useState } from 'react';
import styles from '../ScenarioGenerator.module.css';
import type { SceneItemProps } from '../SceneItem';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { useSceneStateContext } from '../hooks/SceneStateContext';
import { deriveSceneInputFamily } from '../utils';
import { SceneFrameTuningDialog } from './SceneFrameTuningDialog';
import { SceneLastFrameDialog } from './SceneLastFrameDialog';
import { hasSceneFrameSettings, sceneFrameSettingsFromScene } from '../hooks/sceneFrameSettingsQueue';

type SceneFrameTabsProps = Pick<SceneItemProps,
  'scenario' | 'scene' | 'sceneKey' | 'nextSceneContinuesPrevious'
> & {
  /** faceSafe シーン (Seedance×フレームモード×顔あり)。イラスト絵コンテの last frame は
   *  i2v / 連続シーン流用に使えないため、最後のフレーム UI を丸ごと非表示にする (§4.6)。 */
  blockFirstFrameInput?: boolean;
  /** 「延長」(前の動画とつなげる) が実効 ON か。telop_in_frame はフレーム静止画への
   *  焼き込み専用 (動画プロンプトはシーンの telop テキストを別経路で常に併記) のため、
   *  フレームを使わない延長シーンではテロップアイコンを非表示にする。
   *  商品アイコンは動画側の ASIN 商品画像参照 (v2v 系) にも効くため残す。 */
  frameLinkActive?: boolean;
};

/**
 * フレームオプション (再生成行 / 「調整」「最後のフレーム」のアイコン行とそれぞれのダイアログ)。
 * 参照画像モードでは丸ごと非表示。商品タブ・テロップタブは廃止、最後のフレームは折りたたみから
 * ボタン+ダイアログへ (docs/features/scene-frame-generation-tuning-plan.md §2.3・§4.4)。
 */
export function SceneFrameTabs({
  scenario,
  scene,
  sceneKey: key,
  nextSceneContinuesPrevious,
  blockFirstFrameInput = false,
  frameLinkActive = false,
}: SceneFrameTabsProps) {
  const {
    heroImageUrl,
    asin,
    manualProductId,
    creditBadgeText,
    isCreditInsufficient,
    isStorageExceeded,
    handleGenerateScenePreview,
    openFrameDropdown,
  } = useSceneHandlers();
  const {
    scenePreviews, scenePreviewLoading, scenePreviewGenerated,
    sceneLastFrames, lastFrameLoading,
  } = useSceneStateContext();

  const hasPreview = (scenePreviews[key] || []).length > 0;
  // 「調整」(シーン単位のフレーム生成設定) ダイアログ。設定ありはボタンの色で示す
  const [tuningOpen, setTuningOpen] = useState(false);
  const frameTuningActive = hasSceneFrameSettings(sceneFrameSettingsFromScene(scene));
  // 「最後のフレーム」ダイアログ (§4.4)。設定済み・生成中・次のシーンへ継続 をボタンの見た目で示す
  const [lastFrameOpen, setLastFrameOpen] = useState(false);
  const isContinuedByNext = !!nextSceneContinuesPrevious && scenario.frame_mode === 'first_and_last';
  const hasLastFrame = !!sceneLastFrames[key];
  const lastFrameBusy = !!lastFrameLoading[key];
  const lastFrameTooltip = lastFrameBusy
    ? '最後のフレームを生成中'
    : isContinuedByNext
      ? '最後のフレーム (次のシーンへ継続するため不要)'
      : hasLastFrame ? '最後のフレーム (設定済み)' : '最後のフレーム (未設定)';
  // シーンの入力系統 (P2 step 3。docs/features/scene-location-and-production-mode-plan.md
  // §3.1)。videoGenerationMode (シナリオ単位) の代わりにシーン単位で判定する。
  const sceneFamily = deriveSceneInputFamily(scenario, scene);


  return (
    <>
                                {/* フレームオプション（タブ式アイコンUI）— 参照画像モードでは非表示 */}
                                  {sceneFamily !== 'reference' && (
                                  <div className={styles.previewExtraControls}>
                                    {/* 再生成ボタン行（プレビュー画像がある場合のみ）。
                                        延長実効 ON 中はフレームを使わないため、連鎖で
                                        プレビューが入っていても表示しない */}
                                    {hasPreview && !frameLinkActive && (
                                      <div className={styles.frameRegenerateRow}>
                                        <div className={`${styles.frameGenerateGroup} ${styles.frameGenerateGroupSmall} ${scenePreviewLoading[key] ? 'ai-loading-active' : ''}`}>
                                          <button
                                            type="button"
                                            className={styles.frameGenerateButton}
                                            onClick={() => handleGenerateScenePreview(scenario, scene)}
                                            disabled={
                                              scenePreviewLoading[key]
                                              || (!heroImageUrl && !asin && !manualProductId)
                                              || isCreditInsufficient
                                              || isStorageExceeded
                                            }
                                            data-tooltip={`${creditBadgeText}ポイントを消費します`}
                                            data-tooltip-delay=""
                                          >
                                            {scenePreviewLoading[key] ? (
                                              <>
                                                生成中
                                                <span className="ai-loading-dots">
                                                  <span>.</span>
                                                  <span>.</span>
                                                  <span>.</span>
                                                </span>
                                              </>
                                            ) : (
                                              <>
                                                フレーム再生成
                                                <span className={styles.creditBadge}>{creditBadgeText}</span>
                                              </>
                                            )}
                                          </button>
                                          <div className={styles.frameModelDropdown}>
                                            <button
                                              type="button"
                                              className={styles.frameModelDropdownTrigger}
                                              onClick={(e) => openFrameDropdown(`first-regenerate-${key}`, e.currentTarget)}
                                              disabled={scenePreviewLoading[key]}
                                            >
                                              <span className={styles.frameModelArrow}>▼</span>
                                            </button>
                                          </div>
                                        </div>
                                        {scenePreviewGenerated[key] && scenePreviewLoading[key] && (
                                          <button
                                            type="button"
                                            className={styles.frameAddButton}
                                            onClick={() => handleGenerateScenePreview(scenario, scene, { additive: true })}
                                            disabled={
                                              (!heroImageUrl && !asin && !manualProductId)
                                              || isCreditInsufficient
                                              || isStorageExceeded
                                            }
                                            data-tooltip={`追加生成（${creditBadgeText}pt）`}
                                            data-tooltip-delay=""
                                          >
                                            +1
                                          </button>
                                        )}
                                      </div>
                                    )}
                                    {/* タブアイコン */}
                                    <div className={styles.frameTabIcons}>
                                      {!frameLinkActive && (
                                        <button
                                          type="button"
                                          className={`${styles.frameTabIcon} ${tuningOpen ? styles.frameTabIconActive : ''} ${frameTuningActive ? styles.frameTabIconHasSettings : ''}`}
                                          onClick={() => setTuningOpen(true)}
                                          data-tooltip={frameTuningActive ? 'フレーム生成の調整 (設定あり)' : 'フレーム生成の調整 (追加プロンプト)'}
                                          aria-label="フレーム生成の調整"
                                        >
                                          <span
                                            className={styles.frameTabIconMask}
                                            style={{ WebkitMaskImage: 'url(/content_pen_write.svg)', maskImage: 'url(/content_pen_write.svg)' }}
                                          />
                                        </button>
                                      )}
                                      {/* 最後のフレーム: faceSafe (§4.6)・延長実効 ON では出さない (現行の非表示条件) */}
                                      {!blockFirstFrameInput && !frameLinkActive && (
                                        <button
                                          type="button"
                                          className={`${styles.frameTabIcon} ${lastFrameOpen ? styles.frameTabIconActive : ''} ${hasLastFrame ? styles.frameTabIconHasSettings : ''} ${lastFrameBusy ? styles.frameTabIconLoading : ''} ${isContinuedByNext && !hasLastFrame ? styles.frameTabIconDisabled : ''}`}
                                          onClick={() => setLastFrameOpen(true)}
                                          data-tooltip={lastFrameTooltip}
                                          aria-label="最後のフレーム"
                                        >
                                          <span
                                            className={styles.frameTabIconMask}
                                            style={{ WebkitMaskImage: 'url(/scene_last_frame.svg)', maskImage: 'url(/scene_last_frame.svg)', opacity: isContinuedByNext && !hasLastFrame ? 0.35 : 1 }}
                                          />
                                        </button>
                                      )}
                                    </div>
                                    {tuningOpen && (
                                      <SceneFrameTuningDialog
                                        scenario={scenario}
                                        scene={scene}
                                        busy={!!scenePreviewLoading[key]}
                                        onClose={() => setTuningOpen(false)}
                                      />
                                    )}
                                    {lastFrameOpen && !blockFirstFrameInput && !frameLinkActive && (
                                      <SceneLastFrameDialog
                                        scenario={scenario}
                                        scene={scene}
                                        sceneKey={key}
                                        isContinuedByNext={isContinuedByNext}
                                        onClose={() => setLastFrameOpen(false)}
                                      />
                                    )}
                                  </div>
                                  )}
    </>
  );
}
